import { useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { StackStarIcon } from '@hugeicons/core-solid-rounded'
import { useLingui } from '@lingui/react/macro'

import usePaginatedGoodies, {
  QueryVariables
} from '../hooks/usePaginatedGoodies'

import * as Table from '@/components/ui/Table'
import Loading from '@/components/ui/Loading'
import GoodieTableHead from './GoodieTableHead'
import GoodieRow from './GoodieRow'

function LoadingRow() {
  return (
    <Table.Row className="pointer-events-none">
      <Table.Cell colSpan={7}>
        <div className="flex flex-row justify-center p-4">
          <Loading />
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

interface GoodieTableProps {
  variables: QueryVariables
  selectedIds: Set<number>
  disabledIds?: Set<number>
  hideButtons?: boolean
  hideCheckbox?: boolean
  shouldSelectInsteadOfEdit?: boolean
  onSelect: (goodie: GraphQL.GoodieFragmentFragment) => void
  onChangeSort: (
    field: GraphQL.GoodiesSortField,
    direction: GraphQL.SortDirection
  ) => void
}

export default function GoodieTable(props: GoodieTableProps) {
  const {
    variables,
    selectedIds,
    disabledIds,
    hideButtons,
    hideCheckbox = false,
    shouldSelectInsteadOfEdit = false,
    onSelect,
    onChangeSort
  } = props

  const { direction, field } = variables.sort

  const {
    items: goodies,
    page,
    metadata,
    loading,
    loadPage
  } = usePaginatedGoodies(variables)

  const { t } = useLingui()

  const { totalPages, totalEntries } = useMemo(() => {
    return {
      totalPages: metadata?.totalPages,
      totalEntries: metadata?.totalEntries
    }
  }, [metadata])

  const hasResults = useMemo(
    () => !!totalEntries && totalEntries > 0,
    [totalEntries]
  )

  return (
    <Table.Root>
      <GoodieTableHead
        sortDirection={direction}
        sortField={field}
        hideCheckbox={hideCheckbox}
        onChangeSort={onChangeSort}
      />
      <Table.Body>
        {goodies &&
          goodies.map(function (goodie) {
            const isDisabled = disabledIds?.has(goodie.id) ?? false

            return (
              <GoodieRow
                key={goodie.id}
                id={goodie.id}
                hideButtons={!!hideButtons}
                hideCheckbox={hideCheckbox}
                isDisabled={isDisabled}
                shouldSelectInsteadOfEdit={shouldSelectInsteadOfEdit}
                isSelected={selectedIds.has(goodie.id)}
                onSelect={() => onSelect(goodie)}
              />
            )
          })}
        {!hasResults && !loading && (
          <Table.Empty
            Icon={StackStarIcon}
            text={t`No Goodies found`}
            toText={t`Create Goodie`}
            to="/create-goodie"
          />
        )}
        {loading && <LoadingRow />}
      </Table.Body>
      {hasResults && page && (
        <Table.PaginationCaption
          totalEntries={totalEntries!}
          currentPage={page}
          totalPages={totalPages!}
          onChangePage={loadPage}
        />
      )}
    </Table.Root>
  )
}
